<template>
	<div class="stickycomponent">
		<v-card class="elevation-0 pa-0 mb-6">
			<v-sheet height="64">
				<v-toolbar flat color="white" class="responsive-toolbar">
					<v-btn icon @click="refreshCalander"><v-icon>mdi-refresh</v-icon></v-btn>

					<v-toolbar-title class="font-weight-bold">{{ title }}</v-toolbar-title>

					<v-btn fab text small color="grey" @click="prev">
						<v-icon small>mdi-arrow-left</v-icon>
					</v-btn>
					<v-btn fab text small color="grey" @click="next">
						<v-icon small>mdi-arrow-right</v-icon>
					</v-btn>
					<v-spacer></v-spacer>
					<div v-if="$vuetify.breakpoint.width >= 1264" class="d-flex flex-row justify-end grey--text lighten-1">
						<span class="mr-2"> <v-icon small color="light-green darken-1">mdi-circle</v-icon> Confirmed </span>

						<span class="mr-2"> <v-icon small color="light-blue darken-2">mdi-circle</v-icon> Request </span>

						<span class="mr-2"> <v-icon small color="yellow darken-3">mdi-circle</v-icon> Public Offer </span>
					</div>
					<v-btn class="mr-4" @click="setToday">Today</v-btn>
					<v-menu bottom right>
						<template #activator="{ on }">
							<v-btn v-on="on">
								<span>{{ typeToLabel[type] }}</span>
								<v-icon right>mdi-menu-down</v-icon>
							</v-btn>
						</template>
						<v-list>
							<v-list-item @click="type = 'day'">
								<v-list-item-title>Day</v-list-item-title>
							</v-list-item>
							<v-list-item @click="type = 'week'">
								<v-list-item-title>Week</v-list-item-title>
							</v-list-item>
							<v-list-item @click="type = 'month'">
								<v-list-item-title>Month</v-list-item-title>
							</v-list-item>
							<v-list-item @click="type = '4day'">
								<v-list-item-title>4 days</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>
				</v-toolbar>
			</v-sheet>
			<v-sheet v-if="$vuetify.breakpoint.width < 1264" height="32">
				<div class="d-flex flex-row justify-center grey--text lighten-1">
					<span class="mr-1"> <v-icon small color="light-green darken-1">mdi-circle</v-icon> Confirmed Booking </span>

					<span class="mr-1"> <v-icon small ma-2 color="light-blue darken-2">mdi-circle</v-icon> Booking Request </span>

					<span class="mr-1"> <v-icon small ma-2 color="primary">mdi-circle</v-icon> Offer </span>
				</div>
			</v-sheet>
			<v-sheet height="600">
				<v-calendar
					ref="calendar"
					v-model="focus"
					color="primary"
					:events="offers"
					:event-color="getEventColor"
					:now="today"
					:type="type"
					:event-height="20"
					:short-weekdays="false"
					@click:event="showEvent"
					@click:more="viewDay"
					@click:date="viewDay"
					@change="updateRange"
				>
					<template #day>
						<!--can slot in data into the day scope here -->
					</template>
				</v-calendar>

				<v-menu v-model="selectedOpen" :close-on-content-click="false" :activator="selectedElement" offset-x>
					<v-card color="grey lighten-4" min-width="350px" flat>
						<v-toolbar :color="selectedEvent.color" dark>
							<v-btn icon>
								<v-icon>mdi-pencil</v-icon>
							</v-btn>
							<v-toolbar-title>{{ selectedEvent.name }}</v-toolbar-title>
							<v-spacer></v-spacer>
							<v-btn icon>
								<v-icon>mdi-trash</v-icon>
							</v-btn>
							<v-btn icon>
								<v-icon>mdi-dots-vertical</v-icon>
							</v-btn>
						</v-toolbar>
						<v-card-text>
							<span>{{ selectedEvent.details }}</span>
						</v-card-text>
						<v-card-actions>
							<v-btn text color="secondary" @click="selectedOpen = false">Close</v-btn>
							<v-spacer />
							<v-btn text color="red" @click="deleteOffer(selectedEvent)">Delete</v-btn>
						</v-card-actions>
					</v-card>
				</v-menu>
			</v-sheet>
		</v-card>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { Booking } from '@/models/booking.model';
import { BookingRequest } from '@/models/availability.model';
import { Offer } from '@/models/offer.model';
import router from '@/router';
import moment from 'moment';
import { bus } from '@/main';
import { AVAILABILITY_API_ENDPOINTS } from '@/utils/api/endpoints';
import { getRelevantProfessions } from '@utils';

import axios from 'axios';
import store from '@/store';

export default {
	name: 'PracticeDashboardBigCalendar',

	data: () => ({
		today: null,
		API_URL: store.getters.getAPI,
		focus: '',
		type: 'month',
		typeToLabel: {
			month: 'Month',
			week: 'Week',
			day: 'Day',
			'4day': '4 Days',
		},
		start: null,
		end: null,
		selectedEvent: {},
		selectedElement: null,
		selectedOpen: false,
		events: [],
		offers: [],
		bookings: [],
	}),

	computed: {
		...mapGetters('bookings', ['companyConfirmedBookings', 'companyBookings']),

		...mapGetters('offers', ['companyOffersPosted', 'companyBookingRequests']),

		...mapGetters('offers', { offersGetter: 'offers' }),

		...mapGetters(['configs', 'user', 'company']),

		title() {
			const { start, end } = this;
			if (!start || !end) {
				return '';
			}

			const startM = moment(typeof start.date === 'string' ? start.date : start);
			const endM = moment(typeof end.date === 'string' ? end.date : end);
			if (!startM.isValid() || !endM.isValid()) {
				return '';
			}

			const startMonth = startM.format('MMMM');
			const endMonth = endM.format('MMMM');
			const suffixMonth = startMonth === endMonth ? '' : endMonth;

			const startYear = startM.year();
			const endYear = endM.year();
			const suffixYear = startYear === endYear ? '' : endYear;

			const startDay = startM.date() + this.nth(startM.date());
			const endDay = endM.date() + this.nth(endM.date());

			switch (this.type) {
				case 'month':
					return `${startMonth} ${startYear}`;
				case 'week':
				case '4day':
					return `${startMonth} ${startDay} ${startYear} - ${suffixMonth} ${endDay} ${suffixYear}`;
				case 'day':
					return `${startMonth} ${startDay} ${startYear}`;
			}
			return '';
		},
	},

	created() {
		bus.$on('UpdateCalendar', this.refreshCalander);
	},

	mounted() {
		this.$refs.calendar.checkChange();
	},

	destroyed() {
		bus.$off('UpdateCalendar');
	},

	methods: {
		...mapActions('bookings', ['LoadCompanyBookings']),

		...mapActions('offers', ['LoadCompanyOffers']),

		...mapActions('availability', ['_LoadAvailability']),

		gotoVerification() {
			router.push('locum/verification');
		},

		gotoCompleteProfile() {
			router.push('/locum/complete-signup');
		},

		viewDay({ date }) {
			this.focus = date;
			this.type = 'day';
		},

		getEventColor(event) {
			return event.color;
		},

		setToday() {
			this.focus = this.today;
		},

		prev() {
			this.$refs.calendar.prev();
		},

		next() {
			this.$refs.calendar.next();
		},

		showEvent({ nativeEvent, event }) {
			const open = () => {
				const eventObj = event.eventObject;
				store.dispatch('setPracticeOfferSelected', eventObj);

				if (event.type === 'avail-booking-request') {
					this.practiceViewRequestSent(event);
				} else if (event.type === 'offer') {
					this.viewOffer(eventObj);
				} else if (event.type === 'booking-request') {
					this.practiceViewRequestSent(event);
				} else {
					this.viewBooking(eventObj);
				}
			};

			if (this.selectedOpen) {
				this.selectedOpen = false;
				setTimeout(open, 10);
			} else {
				open();
			}

			nativeEvent.stopPropagation();
		},

		viewOffer(offer) {
			let locumId, booking_dates, booking_dates_times;

			const { offers_requests, available_dates, start_time, end_time, lunch, notes, id: offerId, profession, rates, experience, accountid, skills, request_dates } = offer;

			if (offers_requests && offers_requests[0]) {
				const { booking_dates: bookingDates, booking_dates_times, id: requestId } = offers_requests[0];
				booking_dates = bookingDates;
				booking_dates_times, (locumId = requestId);
			}
			if (available_dates && !booking_dates) {
				booking_dates = available_dates;
				locumId = null;
			}

			const booking = {
				start_time,
				end_time,
				lunch,
				notes,
				booking_dates,
				booking_dates_times,
				id: offerId,
				profession,
				rates,
				experience,
				accountid,
				skills,
				request_dates: request_dates ?? null,
			};

			store.dispatch('setPracticeViewingLocum', [{ id: locumId, booking, source: 'viewPostedOffer' }]);
			bus.$emit('change-context', 'view-locum-details-card');
			bus.$emit('update-component');
		},

		viewBooking(booking) {
			const {
				booking_dates,
				booking_dates_times,
				professional,
				start_time,
				end_time,
				lunch,
				notes,
				id: bookingId,
				profession,
				rates,
				experience,
				accountid,
				skills,
				request_dates,
			} = booking;

			const bookingObj = {
				start_time,
				end_time,
				lunch,
				notes,
				booking_dates,
				booking_dates_times,
				id: bookingId,
				profession,
				rates,
				experience,
				accountid,
				skills,
				request_dates,
			};
			store.dispatch('setPracticeViewingLocum', [{ id: professional.id, booking: bookingObj, source: 'viewUpcomingBooking' }]);
			bus.$emit('change-context', 'view-locum-details-card');
			bus.$emit('update-component');
		},

		practiceViewRequestSent(event) {
			let booking = {};
			let professionalId = null;
			professionalId = event.eventObject.professionalId;
			booking = event.eventObject;
			booking.availabilityId = event.id;
			booking.request_dates = event.request_dates;

			if (!professionalId && booking.offers_requests[0]) {
				professionalId = booking.offers_requests[0].id;
			}

			store.dispatch('setPracticeViewingLocum', [{ id: professionalId, booking, source: 'viewBookingRequestSent' }]);
			bus.$emit('change-context', 'view-locum-details-card');
			bus.$emit('update-component');
		},

		nth(d) {
			return d > 3 && d < 21 ? 'th' : ['th', 'st', 'nd', 'rd', 'th', 'th', 'th', 'th', 'th', 'th'][d % 10];
		},

		async getAvailabilityBookingRequests(startDate, endDate) {
			/* TODO: The API needs to be extended to be able to do this filtering on the server side */

			const { industry } = this.company;
			const professions = getRelevantProfessions(industry, this.configs.industries, {
				seeAllProfessionCategories: !!this.tenant?.settings?.workflow?.companyProfile?.seeAllProfessionCategories,
			});

			const availabilities = [];

			for (let profession of professions) {
				const response = await this._LoadAvailability({
					endPoint: AVAILABILITY_API_ENDPOINTS.SEARCH,
					params: {
						profession,
						startDate,
						endDate,
					},
				});

				if (response && response.data && response.data.Items && response.data.Items.length > 0) {
					availabilities.push(...response.data.Items);
				}
			}

			const getDateField = set => set['request']['booking_dates'];

			const getBookingEntry = (start, end, { avail, request }) => ({
				id: avail.id,
				name: 'Booking Request',
				color: 'light-blue darken-2',
				type: 'avail-booking-request',
				eventObject: {
					date: avail.date,
					start_time: request.start_time,
					end_time: request.end_time,
					lunch: request.lunch,
					notes: request.notes,
					booking_dates: request.booking_dates,
					id: request.id,
					profession: avail.professional.profession,
					rates: request.rates,
					experience: avail.professional.experience,
					professionalId: avail.professional.id,
					request_dates: request.request_dates,
				},
				offer_type: Offer.TYPES.PRIVATE,
				available_dates: avail.available_dates,
				start: request.request_dates.length === 0 ? start + ' ' + request.start_time : start + ' ' + moment(request.request_dates[0].start_time).format('HH:mm'),
				end: end + ' ' + request.end_time,
			});

			let results = [];
			for (let avail of availabilities) {
				if (avail.booking_requests && avail.booking_requests.length > 0) {
					const filteredRequests = avail.booking_requests?.filter(
						request => !(request.status === BookingRequest.STATUS.CANCELLED || request.status === BookingRequest.STATUS.REJECTED) && request.id == this.company.id
					);
					const mappedRequests = filteredRequests.map(request => ({ avail, request }));
					const bookingRequests = (await this.getGroupedDates(mappedRequests, getDateField, getBookingEntry)) || [];
					results = [...results, ...bookingRequests];
				}
			}

			return results.filter(x => {
				const exists = this.bookings.find(y => y.start === x.start && y.end === x.end);
				return exists ? false : true;
			});
		},

		async getConfirmedBookings(startDate, endDate) {
			await this.LoadCompanyBookings({
				startDate,
				endDate,
			});

			const bookingsTemp = this.companyConfirmedBookings.filter(
				booking =>
					booking.status !== Booking.STATUS.CANCELLED && (moment(booking.start_date).isSame(startDate, 'month') || moment(booking.end_date).isSame(endDate, 'month'))
			);

			const getDateField = set => set['booking_dates'];

			const getBookingEntry = (start, end, booking) => ({
				id: booking.id,
				name: 'Confirmed Booking',
				start: booking.request_dates.length === 0 ? start + ' ' + booking.start_time : start + ' ' + moment(booking.request_dates[0].start_time).format('HH:mm'),
				end: end + ' ' + booking.end_time,
				color: 'light-green darken-1',
				type: 'confirmed-booking',
				eventStart: booking.start_date,
				eventObject: booking,
			});

			const confirmedBookings = (await this.getGroupedDates(bookingsTemp, getDateField, getBookingEntry)) || [];

			return confirmedBookings;
		},

		async getOffers(startDate, endDate) {
			await this.LoadCompanyOffers({
				startDate,
				endDate,
			});

			const offersTemp = this.companyOffersPosted.filter(offer => offer.offer_type === Offer.TYPES.PUBLIC);

			const getOfferDateField = set => set['available_dates'];

			const getOfferEntry = (start, end, offer) => {
				const { id, start_time, end_time, offer_type, available_dates, request_dates } = offer;
				return {
					id,
					name: 'Public Offer',
					color: 'yellow darken-3',
					type: 'offer',
					start: request_dates.length === 0 ? start + ' ' + start_time : start + ' ' + moment(request_dates[0].start_time).format('HH:mm'),
					end: end + ' ' + end_time,
					eventObject: offer,
					offer_type: offer_type,
					available_dates: available_dates,
					request_dates: request_dates,
				};
			};

			const getBookingRequestDateField = set => set['offer']['available_dates'];

			const getBookingRequestEntry = (start, end, bookingRequest) => {
				const { offer, request } = bookingRequest;
				let eventObject = offer;
				if (request && request.booking_dates) {
					eventObject.booking_dates = request.booking_dates;
				}
				return {
					id: offer.id,
					name: 'Booking Request',
					color: 'light-blue darken-2',
					type: 'booking-request',
					start: offer.request_dates.length === 0 ? start + ' ' + offer.start_time : start + ' ' + moment(offer.request_dates[0].start_time).format('HH:mm'),
					end: end + ' ' + offer.end_time,
					eventObject,
					offer_type: offer.offer_type,
					available_dates: offer.available_dates,
					request_dates: offer.request_dates,
				};
			};

			const offersPosted = (await this.getGroupedDates(offersTemp, getOfferDateField, getOfferEntry)) || [];

			const bookingRequests = (await this.getGroupedDates(this.companyBookingRequests, getBookingRequestDateField, getBookingRequestEntry)) || [];

			const requests = [...offersPosted, ...bookingRequests];

			return requests;
		},

		async updateRange({ start, end }) {
			const bookings = await this.getConfirmedBookings(start.date, end.date);
			this.bookings = bookings;

			const offers = await this.getOffers(start.date, end.date);
			const availabilityBookingRequqests = await this.getAvailabilityBookingRequests(start.date, end.date);

			this.start = start;
			this.end = end;
			this.offers = [...offers, ...bookings, ...availabilityBookingRequqests];
		},

		async deleteOffer(date) {
			let newdate = moment(date.start);
			let offer1 = newdate.format('YYYY-MM-DD');
			let offer = offer1.toString();

			const token = store.getters.jwtToken;

			await axios
				.delete(this.API_URL + '/offers/company/' + date.id, {
					headers: {
						Authorization: token,
					},
					data: {
						date: offer,
					},
				})
				.then(async () => {
					this.selectedOpen = false;

					await store.commit('DELETE_AVAILABILITY', offer);
					store.commit('PUSH_NOTIFICATION', { component: 'practice-offer-deleted', data: [''] });
					bus.$emit('change-context', 'practice-dashboard-landing-zone');
				})
				.catch(error => {
					console.error(error);
					alert('An error occured');
				})
				.finally(() => {
					bus.$emit('UpdateCalendar');
				});
		},

		refreshCalander() {
			this.updateRange({ start: this.start, end: this.end });
		},

		async getGroupedDates(events, getDatesField, getEvent) {
			const groupedDates = [];

			try {
				const pushEvent = (start, end, event) => {
					const eventBody = getEvent(start, end, event);
					if (eventBody) {
						return groupedDates.push(eventBody);
					}
					return;
				};

				if (!events) {
					return [];
				}

				for (const event of events) {
					let datesGroup = [];

					const eventDates = getDatesField(event);

					if (!eventDates) continue;

					eventDates.forEach((date, index) => {
						const dateMinusOne = moment(date).subtract(1, 'days').format('YYYY-MM-DD');
						const datePlusOne = moment(date).add(1, 'days').format('YYYY-MM-DD');
						const previousDate = eventDates[index - 1];
						const nextDate = eventDates[index + 1];

						const isFirst = index === 0;
						const isLast = index === eventDates.length - 1;

						if (isFirst) {
							if (datePlusOne === nextDate) {
								datesGroup.push(date);
							} else {
								pushEvent(date, date, event);
							}
						}

						if (eventDates.length === 1) {
							return;
						}

						if (isLast) {
							if (dateMinusOne === previousDate) {
								datesGroup.push(date);
								pushEvent(datesGroup[0], datesGroup[datesGroup.length - 1], event);
								datesGroup = [];
							} else {
								pushEvent(date, date, event);
							}
						}

						if (!isLast && !isFirst) {
							if (dateMinusOne === previousDate && datePlusOne === nextDate) {
								datesGroup.push(date);
							}
							if (dateMinusOne === previousDate && datePlusOne !== nextDate) {
								datesGroup.push(date);
								pushEvent(datesGroup[0], datesGroup[datesGroup.length - 1], event);
								datesGroup = [];
							}
							if (dateMinusOne !== previousDate && datePlusOne === nextDate) {
								datesGroup.push(date);
							}
							if (dateMinusOne !== previousDate && datePlusOne !== nextDate) {
								pushEvent(date, date, event);
							}
						}
					});
				}
			} catch (error) {
				console.error('error mapping events');
				return [];
			}
			return groupedDates;
		},
	},
};
</script>

<style scoped>
.v-calendar-weekly,
.v-calendar-monthly {
	border: none;
}

.v-calendar-weekly__head-weekday,
.v-calendar-daily_head-weekday {
	font-size: 16px;
}
</style>
